import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCategories } from '../contexts/CategoryContext';
import { useRecipe } from '../contexts/RecipeContext';
import {
  ENTITY_TYPES,
  ARCHIVE_REASONS,
  getArchive,
  removeFromArchive,
  restoreFromArchive,
  getArchiveContent
} from '../utils/archiveManager';
import '../styles/Archive.css';
import '../styles/DynamicPanel.css';

const TYPE_ICON = {
  [ENTITY_TYPES.CATEGORY]: '📁',
  [ENTITY_TYPES.BOOKMARK]: '🔖',
  [ENTITY_TYPES.RECIPE]: '🍽️',
  [ENTITY_TYPES.SHOPPING_LIST]: '🛒'
};

const Archive = ({ isOpen, onClose, onRestoreBookmark }) => {
  const { t } = useLanguage();
  const { addRestoredCategory } = useCategories();
  const { addRestoredRecipe, addRestoredShoppingList } = useRecipe();

  const [filterType, setFilterType] = useState('all');
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', text }

  // "Inhalt ansehen" - Plain-Text-Content-Snapshot einer Entität
  const [contentView, setContentView] = useState(null); // { entry, loading, content, error }

  const reload = useCallback(async () => {
    setEntriesLoading(true);
    try {
      const data = await getArchive();
      setEntries(data || []);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) reload();
  }, [isOpen, reload]);

  if (!isOpen) return null;

  const filteredEntries = filterType === 'all'
    ? entries
    : entries.filter(e => e.entityType === filterType);

  const handleRestore = async (entry) => {
    setRestoringId(entry.id);
    setFeedback(null);
    try {
      // Das Backend legt beim Restore serverseitig ein neues Objekt an (neue
      // ID) und entfernt danach automatisch den Archiv-Eintrag. Wir müssen
      // hier NICHT mehr selbst addCategory()/createRecipe()/... aufrufen -
      // das würde das Element doppelt anlegen. Stattdessen tragen wir das
      // vom Server zurückgegebene Objekt nur noch lokal in den jeweiligen
      // Context ein.
      const { restored } = await restoreFromArchive(entry.id);

      switch (entry.entityType) {
        case ENTITY_TYPES.CATEGORY:
          addRestoredCategory(restored);
          break;
        case ENTITY_TYPES.BOOKMARK:
          await onRestoreBookmark(restored);
          break;
        case ENTITY_TYPES.RECIPE:
          addRestoredRecipe(restored);
          break;
        case ENTITY_TYPES.SHOPPING_LIST:
          addRestoredShoppingList(restored);
          break;
        default:
          break;
      }

      setEntries(prev => prev.filter(e => e.id !== entry.id));
      setFeedback({ type: 'success', text: t('dataArchive.restoreSuccess') || 'Erfolgreich wiederhergestellt' });
    } catch (err) {
      console.error('Archiv: Wiederherstellen fehlgeschlagen', err);
      // 409 Conflict (z.B. Kategorie-Name existiert schon wieder) wird vom
      // Backend als solcher gemeldet - der Archiv-Eintrag bleibt in diesem
      // Fall serverseitig bestehen, nichts geht verloren.
      const isConflict = /existiert bereits|conflict/i.test(err.message || '');
      setFeedback({
        type: 'error',
        text: isConflict
          ? (t('dataArchive.restoreExists') || 'Existiert bereits, nicht wiederhergestellt')
          : (t('dataArchive.restoreError') || 'Wiederherstellen fehlgeschlagen')
      });
    } finally {
      setRestoringId(null);
    }
  };

  const handleRemove = async (entry) => {
    if (!window.confirm(t('dataArchive.removeConfirm') || 'Diesen Eintrag endgültig aus dem Archiv entfernen? (Kann nicht rückgängig gemacht werden)')) {
      return;
    }
    setRemovingId(entry.id);
    setFeedback(null);
    try {
      await removeFromArchive(entry.id);
      setEntries(prev => prev.filter(e => e.id !== entry.id));
    } catch (err) {
      console.error('Archiv: Entfernen fehlgeschlagen', err);
      setFeedback({ type: 'error', text: t('dataArchive.removeError') || 'Entfernen fehlgeschlagen' });
    } finally {
      setRemovingId(null);
    }
  };

  const handleViewContent = async (entry) => {
    setContentView({ entry, loading: true, content: null, error: null });
    try {
      const content = await getArchiveContent(entry.entityType, entry.originalId);
      setContentView({ entry, loading: false, content, error: content ? null : 'not-found' });
    } catch (err) {
      setContentView({ entry, loading: false, content: null, error: err.message });
    }
  };

  const filters = [
    { key: 'all', label: t('dataArchive.filterAll') || 'Alle', icon: '🗄️' },
    { key: ENTITY_TYPES.CATEGORY, label: t('categories.title') || 'Kategorien', icon: '📁' },
    { key: ENTITY_TYPES.BOOKMARK, label: t('bookmarks') || 'Lesezeichen', icon: '🔖' },
    { key: ENTITY_TYPES.RECIPE, label: t('menu.recipes') || 'Rezepte', icon: '🍽️' },
    { key: ENTITY_TYPES.SHOPPING_LIST, label: t('shoppingList.title') || 'Einkaufslisten', icon: '🛒' }
  ];

  return (
    <div className="dynamic-panel">
      <div className="theme-power archive-power-panel">
        <div className="archive-power-headline">
          <div className="archive-headline-left">
            <span className="archive-headline-icon">🗄️</span>
            <h2 className="archive-headline-title">
              {t('dataArchive.title') || 'Archiv'}
            </h2>
          </div>
        </div>

        <p className="archive-intro-text">
          {t('dataArchive.intro') || 'Gelöschte Kategorien, Lesezeichen, Rezepte und Einkaufslisten landen automatisch hier und können von dort wiederhergestellt werden.'}
        </p>

        <div className="archive-filter-row">
          {filters.map(f => (
            <button
              key={f.key}
              className={`archive-filter-chip ${filterType === f.key ? 'active' : ''}`}
              onClick={() => setFilterType(f.key)}
            >
              <span className="archive-filter-icon">{f.icon}</span>
              {f.label}
              {f.key !== 'all' && (
                <span className="archive-filter-count">
                  {entries.filter(e => e.entityType === f.key).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {feedback && (
          <div className={`archive-feedback archive-feedback-${feedback.type}`}>
            {feedback.type === 'success' ? '✓' : '⚠️'} {feedback.text}
          </div>
        )}

        <div className="archive-list">
          {entriesLoading ? (
            <div className="archive-empty">
              <p>⏳ {t('loading') || 'Lädt...'}</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="archive-empty">
              <p>📭 {t('dataArchive.empty') || 'Keine archivierten Elemente'}</p>
              <p className="archive-empty-hint">
                {t('dataArchive.emptyHint') || 'Gelöschte Kategorien, Lesezeichen, Rezepte und Einkaufslisten erscheinen hier.'}
              </p>
            </div>
          ) : (
            filteredEntries.map(entry => (
              <div key={entry.id} className="archive-item">
                <div className="archive-item-icon">{TYPE_ICON[entry.entityType] || '📦'}</div>
                <div className="archive-item-content">
                  <div className="archive-item-title">{entry.label || (t('dataArchive.noTitle') || '(ohne Titel)')}</div>
                  <div className="archive-item-meta">
                    <span className={`archive-item-badge badge-${entry.reason}`}>
                      {entry.reason === ARCHIVE_REASONS.DELETED
                        ? (t('dataArchive.badgeDeleted') || 'Gelöscht')
                        : (t('dataArchive.badgeSnapshot') || 'Automatische Sicherung')}
                    </span>
                    <span className="archive-item-date">
                      {new Date(entry.archivedAt).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="archive-item-actions">
                  {entry.hasContentSnapshot && (
                    <button
                      className="archive-action-btn content"
                      onClick={() => handleViewContent(entry)}
                      title={t('dataArchive.viewContent') || 'Inhalt ansehen'}
                    >
                      📄 {t('dataArchive.viewContent') || 'Inhalt ansehen'}
                    </button>
                  )}
                  <button
                    className="archive-action-btn restore"
                    onClick={() => handleRestore(entry)}
                    disabled={restoringId === entry.id || removingId === entry.id}
                    title={t('dataArchive.restore') || 'Wiederherstellen'}
                  >
                    {restoringId === entry.id ? '⏳' : '♻️'} {t('dataArchive.restore') || 'Wiederherstellen'}
                  </button>
                  <button
                    className="archive-action-btn remove"
                    onClick={() => handleRemove(entry)}
                    disabled={restoringId === entry.id || removingId === entry.id}
                    title={t('dataArchive.removePermanently') || 'Endgültig entfernen'}
                  >
                    {removingId === entry.id ? '⏳' : '🗑️'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* "Inhalt ansehen" - Plain-Text-Content-Snapshot mit Pflicht-Quellenangabe */}
      {contentView && (
        <>
          <div className="archive-content-overlay" onClick={() => setContentView(null)} />
          <div className="archive-content-viewer theme-power">
            <div className="archive-content-header">
              <h3>{contentView.entry.label || (t('dataArchive.noTitle') || '(ohne Titel)')}</h3>
              <button className="archive-content-close" onClick={() => setContentView(null)}>✕</button>
            </div>

            <div className="archive-content-body">
              {contentView.loading && <p>⏳ {t('loading') || 'Lädt...'}</p>}

              {!contentView.loading && contentView.error === 'not-found' && (
                <p className="archive-content-notfound">
                  {t('dataArchive.contentNotFound') || 'Kein archivierter Inhalt vorhanden.'}
                </p>
              )}

              {!contentView.loading && contentView.error && contentView.error !== 'not-found' && (
                <p className="archive-content-notfound">
                  ⚠️ {contentView.error}
                </p>
              )}

              {!contentView.loading && contentView.content && (
                <>
                  {/* Pflicht-Quellenangabe (Urheberrecht, § 13 UrhG - siehe
                      TODO_Archiv_Backend.md Abschnitt 5.4/6.6): Quelle, Autor/
                      Seitenname und Abrufdatum müssen bei jeder Anzeige sichtbar sein. */}
                  <div className="archive-content-source">
                    <p>
                      <strong>{t('dataArchive.source') || 'Quelle'}:</strong>{' '}
                      <a href={contentView.content.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {contentView.content.sourceSiteName || contentView.content.sourceUrl}
                      </a>
                    </p>
                    {contentView.content.sourceAuthor && (
                      <p><strong>{t('dataArchive.author') || 'Autor'}:</strong> {contentView.content.sourceAuthor}</p>
                    )}
                    <p className="archive-content-disclaimer">
                      {t('dataArchive.archivedNotice', { date: new Date(contentView.content.capturedAt).toLocaleString() })
                        || `Archivierte Version vom ${new Date(contentView.content.capturedAt).toLocaleString()} – Original: siehe Link oben. Der Inhalt kann sich seitdem geändert haben oder nicht mehr verfügbar sein.`}
                    </p>
                  </div>
                  <div className="archive-content-text">
                    {contentView.content.plainText}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Archive;
